import { useSelector } from 'react-redux';

import { useIsSignedIn } from '@wecasa/auth/src/authentication-context';

import { getCurrentCountry } from '@wecasa/common/src/utils/country';
import { isCleaningReference } from '@wecasa/common/src/utils/universes';

import { CONFIG_BY_COUNTRY } from '../constants/country-config';
import { isRecurringCleaningSelector } from '../ducks/basket';
import { getUniverseRef } from '../ducks/basket-selectors';
import { useGetCesuWalletQuery } from '../queries/cesu-queries';

type UseHasCesuWalletParams = {
  isEnabled?: boolean;
  // Pass true on the steps before the prestation selection: there a wallet hides ITC whatever the frequency.
  isBeforePrestationSelection?: boolean;
};

// ITC is replaced by CESU in the cleaning funnel when the customer has money in the CESU wallet for spot cleaning.
// If the CESU could not load, we keep ITC; isCesuWalletLoading lets one-shot flows wait for the resolved value.
// WARNING: CESU is web-only for the MVP. mobile can't use it because of the different auth mechanism
export const useHasCesuWallet = ({
  isEnabled = true,
  isBeforePrestationSelection = false,
}: UseHasCesuWalletParams = {}) => {
  const isSignedIn = useIsSignedIn();
  const universeReference = useSelector(getUniverseRef);
  const isRecurringCleaning = useSelector(isRecurringCleaningSelector);
  const { isCesuAvailable } = CONFIG_BY_COUNTRY[getCurrentCountry()];
  const shouldFetchCesuWallet =
    isEnabled &&
    isSignedIn &&
    isCesuAvailable &&
    isCleaningReference(universeReference) &&
    (isBeforePrestationSelection || !isRecurringCleaning);

  const { cesuAmount, isLoadingCesuAmount } = useGetCesuWalletQuery({
    enabled: shouldFetchCesuWallet,
    universe: universeReference,
  });

  return {
    // Raw balance, for the callers comparing it to a price and not just to zero.
    // undefined here also means "not fetched", on top of the query's own "not loaded yet".
    cesuAmount: shouldFetchCesuWallet ? cesuAmount : undefined,
    hasCesuWallet: shouldFetchCesuWallet && (cesuAmount ?? 0) > 0,
    // A disabled query reports isLoading forever in react-query v4, hence the enabled guard.
    isCesuWalletLoading: shouldFetchCesuWallet && isLoadingCesuAmount,
  };
};
